import React, { useState, useEffect } from 'react';
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Button,
  Row,
  Col
} from 'reactstrap';
import classnames from 'classnames';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import api from '../../api';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import './StaffList.css';

const StaffList = () => {
  document.title = "Staff List | Pokémon Radiance";
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('staff');
  const [staffList, setStaffList] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStaffList();
  }, []);

  const loadStaffList = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/v1/staff-list", { withCredentials: true });
      if (response.data.success) {
        setStaffList(response.data.staff);
        setRules(response.data.rules);
      }
    } catch (err) {
      setError("Failed to load staff list");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content" style={{ background: "#1d222e", minHeight: "100vh", padding: "2rem" }}>
        <Container fluid>
          <p style={{ color: "#fff", textAlign: "center" }}>Loading Staff List...</p>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content staff-container" style={{ background: "#1d222e", minHeight: "100vh", padding: "2rem" }}>
        <Container fluid>
          <Breadcrumbs title="Staff" breadcrumbItem="Staff List" />

          {/* Header Card */}
          <Card className="mb-4" style={{ background: "#272d39", border: "none", borderRadius: "12px" }}>
            <CardBody>
              <div>
                <h3 style={{ color: "#fff", margin: 0, marginBottom: "0.5rem" }}>Staff List</h3>
                <p style={{ color: "#ccc", margin: 0 }}>
                  Meet our team members and learn about the game rules
                </p>
              </div>
            </CardBody>
          </Card>

          {error && (
            <Card className="mb-4" style={{ background: "#272d39", border: "none", borderRadius: "12px" }}>
              <CardBody>
                <div style={{ color: "#f44336", textAlign: "center" }}>
                  <p>{error}</p>
                  <Button color="primary" onClick={loadStaffList}>Retry</Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Tabs */}
          <Nav tabs className="border-0 mb-3 staff-tabs">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "staff" })}
                onClick={() => setActiveTab("staff")}
                style={{ cursor: "pointer", fontWeight: '600', color: '#fff' }}
              >
                Staff Members
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "rules" })}
                onClick={() => setActiveTab("rules")}
                style={{ cursor: "pointer", fontWeight: '600', color: '#fff' }}
              >
                Game Rules
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab} className="p-0">
            {/* Staff Members Tab */}
            <TabPane tabId="staff">
              <Card className="shadow-sm border-0 rounded" style={{ backgroundColor: '#1d222e' }}>
                <CardBody>
                  <h4 style={{ fontWeight: '600', color: '#fff', marginBottom: '1.5rem' }}>Our Team</h4>
                  <p style={{ color: '#ccc', marginBottom: '2rem' }}>
                    Meet the dedicated staff members who help make Pokémon Radiance an amazing experience for everyone.
                  </p>

                  <div className="d-flex flex-wrap" style={{ gap: '1rem' }}>
                    {staffList.map((staff, index) => (
                      <Card
                        key={index}
                        className="shadow-sm rounded border-0 flex-fill staff-card"
                        style={{ 
                          minWidth: '280px', 
                          cursor: 'pointer'
                        }}
                        onClick={() => navigate(`/playerprofile?user=${staff.username}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <CardBody className="p-3">
                          <Row className="align-items-center">
                            <Col xs="3" className="text-center">
                              <img
                                src={`/avatars/${staff.avatar || 'default'}.png`}
                                alt={staff.username}
                                className="staff-avatar"
                                style={{ 
                                  width: 60, 
                                  height: 60, 
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/playerprofile?user=${staff.username}`);
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'scale(1.1)';
                                  e.target.style.boxShadow = '0 0 10px rgba(255,255,255,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'scale(1)';
                                  e.target.style.boxShadow = '';
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/images/default.png';
                                }}
                              />
                            </Col>
                            <Col xs="6">
                              <div 
                                className="staff-username"
                                style={{ 
                                  color: '#fff', 
                                  fontWeight: '600', 
                                  marginBottom: '0.25rem',
                                  cursor: 'pointer',
                                  display: 'inline-block'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/playerprofile?user=${staff.username}`);
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.textDecoration = 'underline';
                                  e.target.style.color = '#4dabf7';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.textDecoration = 'none';
                                  e.target.style.color = '#fff';
                                }}
                              >
                                {staff.username}
                              </div>
                              <div className={`staff-role role-${staff.role.toLowerCase()}`} style={{ 
                                fontSize: '0.9rem',
                                marginBottom: '0.25rem'
                              }}>
                                {staff.role}
                              </div>
                              <div className="staff-last-active">
                                <span className={`online-indicator online-${staff.is_online ? 'true' : 'false'}`}></span>
                                {staff.is_online ? 'Online now' : `Last active: ${formatDistanceToNow(parseISO(staff.last_active), { addSuffix: true })}`}
                              </div>
                            </Col>
                            <Col xs="3" className="text-center">
                              <div className="d-flex flex-column gap-2">
                                <Button
                                  className="staff-button profile-button"
                                  color="primary"
                                  size="sm"
                                  style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/playerprofile?user=${staff.username}`);
                                  }}
                                >
                                  <i className="fas fa-user"></i> Profile
                                </Button>
                                <Button
                                  className="staff-button message-button"
                                  color="info"
                                  size="sm"
                                  style={{ fontSize: '0.8rem' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/send-message?to=${staff.username}`);
                                  }}
                                >
                                  <i className="fas fa-envelope"></i> Message
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </TabPane>

            {/* Rules Tab */}
            <TabPane tabId="rules">
              <Card className="shadow-sm border-0 rounded" style={{ backgroundColor: '#1d222e' }}>
                <CardBody>
                  <h4 style={{ fontWeight: '600', color: '#fff', marginBottom: '1.5rem' }}>Game Rules</h4>
                  <p style={{ color: '#ccc', marginBottom: '2rem' }}>
                    Please read and follow these rules to ensure a fair and enjoyable experience for all players.
                  </p>

                  <div className="d-flex flex-column" style={{ gap: '1rem' }}>
                    {rules.map((rule) => (
                      <Card
                        key={rule.id}
                        className="shadow-sm rounded border-0"
                        style={{ backgroundColor: '#272d39' }}
                      >
                        <CardBody className="p-3">
                          <div style={{ 
                            color: '#fff', 
                            fontWeight: '600', 
                            marginBottom: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span style={{
                              backgroundColor: '#4CAF50',
                              color: '#fff',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.8rem',
                              fontWeight: '700'
                            }}>
                              {rule.id}
                            </span>
                            {rule.title}
                          </div>
                          <div style={{ color: '#ccc', lineHeight: '1.5' }}>
                            {rule.description}
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StaffList;
